import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ContentPanel, ContentBox } from '../../../../Maps';
import {
  Box,
  Button,
  Typography,
  Stack,
  CircularProgress,
  Modal,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  IconButton,
  FavoriteBorderOutlinedIcon,
  ToolTip,
  Popover
} from '@esgian/esgianui';
import { PageLinkIcon } from '@components';
import { useSegment, useFleets, useTheme } from '@hooks';
import FleetVesselsTable from './FleetVesselsTable';
import FleetTopPortsTable from './FleetTopPortsTable';
import FleetActivityProfile from './FleetActivityProfile';
import FleetAverageSpeed from './FleetAverageSpeed';
import { ACTIVITY_MAP_SEARCH_PARAM_KEYS, SIDE_PANEL_TYPES } from '../constants';
import { useSearchParams } from 'react-router-dom';
import { useMapState } from '../hooks/useMapState';
import { useNavigate } from 'react-router-dom';
import { DATE_TIME_FORMAT } from '@constants';
import moment from 'moment';
import { VoyageType } from '@components/Sections/VoyageAnalytics/VoyageUtils';
import { setTempOngoingVoyageGroup } from '@pages/VoyageAnalytics/hooks/useVoyageGroups';
import CreateFleetModal from '../../../../Maps/DashboardActivityMap/DashboardActivityMapActionWindow/CreateFleetModal';
import { toast } from 'react-toastify';
import AddToFleetPopup from './AddToFleetPopup';

// Fleet selection modal component
const SelectFleetModal = ({
  open,
  onClose,
  fleets,
  onSelectFleet,
  onCreateNewFleet,
  isLoading
}) => {
  const [selectedFleetId, setSelectedFleetId] = useState('');
  const { customScrollbar } = useTheme();

  const handleChange = (event) => {
    setSelectedFleetId(event.target.value);
  };

  const handleConfirm = () => {
    if (selectedFleetId === 'new') {
      onCreateNewFleet();
    } else {
      const selectedFleet = fleets.find((fleet) => fleet.id === selectedFleetId);
      if (selectedFleet) {
        onSelectFleet(selectedFleet);
      }
    }
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ width: '100%', maxHeight: '80vh' }}>
        <Typography variant="h6" gutterBottom>
          Select Fleet
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Choose a fleet to add all vessels or create a new one
        </Typography>

        <Paper sx={{ maxHeight: 300, overflow: 'auto', mb: 2, ...customScrollbar }}>
          <RadioGroup value={selectedFleetId} onChange={handleChange} sx={{ p: 2 }}>
            {fleets.map((fleet) => (
              <FormControlLabel
                key={fleet.id}
                value={fleet.id}
                control={<Radio />}
                label={
                  <Box component="span" display="flex" alignItems="center">
                    <Typography variant="body1">{fleet.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({fleet.vessels.length} vessels)
                    </Typography>
                  </Box>
                }
                sx={{ backgroundColor: 'background.paper' }}
              />
            ))}
            <FormControlLabel
              value="new"
              control={<Radio />}
              label={<Typography variant="body1">Create New Fleet</Typography>}
              sx={{ backgroundColor: 'background.paper' }}
            />
          </RadioGroup>
        </Paper>

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={isLoading || (!selectedFleetId && selectedFleetId !== 'new')}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}>
            {isLoading ? 'Processing...' : 'Continue'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

SelectFleetModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fleets: PropTypes.array.isRequired,
  onSelectFleet: PropTypes.func.isRequired,
  onCreateNewFleet: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};

const FilteredVesselsDetailsPanel = ({ filteredVessels, handleClose }) => {
  const [, setSearchParams] = useSearchParams();
  const { handleVesselSelect } = useMapState();
  const { uriExt, segment } = useSegment();
  const navigate = useNavigate();
  const { savedFleets, createFleet, isCreatingFleet, updateFleetVessels } = useFleets();

  const [previousDays, setPreviousDays] = useState(30);
  const [showCreateFleetModal, setShowCreateFleetModal] = useState(false);
  const [isAddingToFleet, setIsAddingToFleet] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Filter fleets based on segment
  const segmentFilteredFleets = useMemo(() => {
    return savedFleets.filter((fleet) => fleet.segmentId === segment.id);
  }, [savedFleets, segment]);

  const allVessels = useMemo(() => {
    if (!filteredVessels?.length) return [];

    // Map filtered vessels to the format expected by the components
    return filteredVessels.map((vessel) => ({
      vesselImo: vessel.imo,
      vesselName: vessel.vesselName,
      operatorShortName: vessel.operator || vessel.operatorShortName,
      operatorId: vessel.operatorId,
      operatorName: vessel.operatorLongName,
      destination: vessel.destination,
      destinationPortName: vessel.destinationPortName,
      destinationCountryName: vessel.destinationCountryName,
      eta: vessel.eta,
      draught: vessel.draught,
      vesselDraught: vessel.vesselDraught,
      percentDraught: vessel.percentDraught,
      status: vessel.status,
      lat: vessel.lat,
      long: vessel.long,
      speed: vessel.speed,
      course: vessel.course,
      lastUpdate: vessel.lastUpdate
    }));
  }, [filteredVessels]);

  const vesselImos = useMemo(
    () => allVessels.map((v) => v.vesselImo || v.imo).filter(Boolean),
    [allVessels]
  );

  const handleViewVesselPosition = (vesselImo) => {
    // Open vessel details and close the filtered vessels panel
    setSearchParams((prev) => {
      const updated = new URLSearchParams(prev);
      updated.set(ACTIVITY_MAP_SEARCH_PARAM_KEYS.SIDE_PANEL_TYPE, SIDE_PANEL_TYPES.VESSEL_DETAILS);
      updated.set(ACTIVITY_MAP_SEARCH_PARAM_KEYS.SIDE_PANEL_ITEM_ID, vesselImo.toString());
      return updated;
    });

    // Trigger map to focus on vessel
    handleVesselSelect(vesselImo);
  };

  const handleViewVoyageGroup = () => {
    if (!allVessels?.length) return;

    // Create a temp voyage group using all filtered vessels
    const tempVoyageGroup = {
      id: `ongoingVoyageGroup~${previousDays}~temp`,
      name: `Filtered Vessels - Voyages`,
      type: VoyageType.ONGOING,
      voyages: allVessels.map((vessel) => ({
        imo: vessel.vesselImo,
        vesselName: vessel.vesselName,
        departureDate: moment()
          .startOf('day')
          .subtract(previousDays + 1, 'days')
          .format(DATE_TIME_FORMAT),
        note: vessel.note || ''
      }))
    };

    // Save to localStorage
    setTempOngoingVoyageGroup(tempVoyageGroup);

    // Navigate to voyage group page
    navigate(`${uriExt}/voyage-group/${tempVoyageGroup.id}`);
  };

  const handleCreateFleet = async (values) => {
    try {
      setIsAddingToFleet(true);
      const fleetData = {
        ...values,
        color: values.color,
        segmentId: segment?.id
      };

      await createFleet(fleetData);
      setShowCreateFleetModal(false);

      setTimeout(() => {
        const newFleet = savedFleets.find((f) => f.name === values.name);
        if (newFleet) {
          addVesselsToFleet(newFleet);
        }
      }, 2000);
    } catch (error) {
      console.error('Error creating fleet:', error);
      toast.error('Failed to create fleet. Please try again.');
    } finally {
      setIsAddingToFleet(false);
    }
  };

  const addVesselsToFleet = async (fleet) => {
    if (!fleet || !filteredVessels.length) return;

    try {
      const vesselsToAdd = filteredVessels.map((vessel) => ({
        vesselName: vessel.vesselName,
        vesselImo: vessel.imo,
        operator: vessel.operator || vessel.operatorShortName || ''
      }));

      await updateFleetVessels({
        fleetId: fleet.id,
        added: vesselsToAdd,
        removed: []
      });

      toast.success(`Added ${vesselsToAdd.length} vessels to "${fleet.name}"`);
    } catch (error) {
      console.error('Error adding vessels to fleet:', error);
      toast.error('Failed to add vessels to the fleet. Please try again.');
    } finally {
      setIsAddingToFleet(false);
      setAnchorEl(null);
    }
  };

  const handleShowFleetSelection = (event) => {
    if (segmentFilteredFleets.length === 0) {
      // If no fleets exist yet, directly show create fleet modal
      setShowCreateFleetModal(true);
    } else {
      // Show the popover
      setAnchorEl(event.currentTarget);
    }
  };

  if (!filteredVessels?.length) {
    return null;
  }

  return (
    <ContentPanel
      title={
        <ContentBox>
          <Box>
            <Typography variant="h6" display="flex" alignItems="center" gap={1}>
              Filtered Vessels Overview ({filteredVessels.length})
              <ToolTip title="Add to Fleet" placement="bottom">
                <span>
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={handleShowFleetSelection}
                    disabled={!allVessels?.length || isAddingToFleet}>
                    {isAddingToFleet ? (
                      <CircularProgress size={16} />
                    ) : (
                      <FavoriteBorderOutlinedIcon />
                    )}
                  </IconButton>
                </span>
              </ToolTip>
            </Typography>
            <Stack spacing={2}>
              <Typography variant="body2">Total Vessels: {allVessels?.length || 0}</Typography>
            </Stack>
          </Box>
        </ContentBox>
      }
      footerActions={
        <Box textAlign="center">
          <Button
            startIcon={<PageLinkIcon />}
            sx={{ textTransform: 'none', color: 'black' }}
            onClick={handleViewVoyageGroup}
            disabled={!allVessels?.length}>
            View Voyage Group
          </Button>
        </Box>
      }
      onClose={handleClose}>
      <Box display="flex" flexDirection="column" gap={2}>
        <ContentBox skeletonHeight="300px">
          <FleetTopPortsTable
            vesselImos={vesselImos}
            segmentId={segment.id}
            setPreviousDays={setPreviousDays}
          />
        </ContentBox>

        <ContentBox>
          <FleetVesselsTable
            vessels={allVessels}
            height="400px"
            onViewVesselPosition={handleViewVesselPosition}
            fileName="filtered-vessels"
            showFleetInfo={false}
          />
        </ContentBox>

        <ContentBox>
          <FleetAverageSpeed vesselImos={vesselImos} fleetName="Filtered Vessels" />
        </ContentBox>

        <ContentBox>
          <FleetActivityProfile vesselImos={vesselImos} fleetName="Filtered Vessels" />
        </ContentBox>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}>
        <AddToFleetPopup
          savedFleets={segmentFilteredFleets}
          onCreateANewFleet={() => {
            setAnchorEl(null);
            setShowCreateFleetModal(true);
          }}
          onAddToFleet={addVesselsToFleet}
          isAddingToFleet={isAddingToFleet}
        />
      </Popover>

      {/* Create Fleet Modal */}
      <CreateFleetModal
        open={showCreateFleetModal}
        onClose={() => setShowCreateFleetModal(false)}
        onSubmit={handleCreateFleet}
        isSubmitting={isCreatingFleet || isAddingToFleet}
      />
    </ContentPanel>
  );
};

FilteredVesselsDetailsPanel.propTypes = {
  filteredVessels: PropTypes.array.isRequired,
  handleClose: PropTypes.func
};

export default FilteredVesselsDetailsPanel;
